<script>
  import { onMount } from "svelte";
  import CardStats from "../../components/Cards/CardStats.svelte";
  import CardPieChart from "../../components/Cards/CardPieChart.svelte";
  import { portfolio, totalValue, cashBalance, buyingPower, pendingOrders, dayChange } from "../../stores/portfolio.js";
  import { pies, piesList } from "../../stores/pies.js";

  let loading = true;

  onMount(async () => {
    await Promise.all([
      portfolio.fetchSummary(),
      portfolio.fetchPositions(),
      pies.fetchPies(),
    ]);
    loading = false;
  });

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  function formatPercent(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }

  $: portfolioAllocation = $portfolio.positions.map(p => ({
    symbol: p.symbol,
    percentage: parseFloat(((p.market_value / $totalValue) * 100).toFixed(2)) || 0,
  }));
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Dashboard</h1>
  <p class="text-sm text-ink-secondary mt-1">Your portfolio at a glance.</p>
</header>

<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
  <CardStats
    statSubtitle="Total value"
    statTitle={loading ? "..." : formatCurrency($totalValue)}
    statArrow={$dayChange.amount >= 0 ? "up" : "down"}
    statPercent={Math.abs($dayChange.percent).toFixed(2)}
    statPercentColor={$dayChange.amount >= 0 ? "text-emerald-600" : "text-red-600"}
    statDescription="Today's change"
    statIconName="fas fa-wallet"
    statIconBg="bg-emerald-50"
    statIconColor="text-moira"
  />
  <CardStats
    statSubtitle="Cash balance"
    statTitle={loading ? "..." : formatCurrency($cashBalance)}
    statArrow="up"
    statPercent={$pendingOrders > 0 ? formatCurrency($pendingOrders) : ""}
    statPercentColor="text-ink-tertiary"
    statDescription={$pendingOrders > 0 ? "in pending orders" : "Available to invest"}
    showPercent={false}
    showArrow={false}
    statIconName="fas fa-dollar-sign"
    statIconBg="bg-indigo-50"
    statIconColor="text-accent"
  />
  <CardStats
    statSubtitle="Available to invest"
    statTitle={loading ? "..." : formatCurrency($buyingPower)}
    statPercent=""
    statPercentColor="text-ink-tertiary"
    statDescription="Buying power after pending orders"
    showPercent={false}
    showArrow={false}
    statIconName="fas fa-bolt"
    statIconBg="bg-amber-50"
    statIconColor="text-warning"
  />
  <CardStats
    statSubtitle="Positions"
    statTitle={loading ? "..." : $portfolio.positions.length.toString()}
    statPercent={$piesList.length.toString()}
    statPercentColor="text-ink-tertiary"
    statDescription={$piesList.length === 1 ? "pie" : "pies"}
    showPercent={false}
    showArrow={false}
    statIconName="fas fa-list"
    statIconBg="bg-blue-50"
    statIconColor="text-info"
  />
</div>

<div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6 items-start">
  <div class="xl:col-span-2 card overflow-hidden">
    <div class="px-6 pt-6 pb-4">
      <h3 class="text-lg font-semibold text-ink">Your Positions</h3>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full border-collapse">
        <thead>
          <tr>
            <th class="th">Symbol</th>
            <th class="th text-right">Shares</th>
            <th class="th text-right">Market Value</th>
            <th class="th text-right">Gain/Loss</th>
          </tr>
        </thead>
        <tbody>
          {#if loading}
            {#each Array(4) as _}
              <tr>
                <td class="td"><div class="skeleton h-4 w-16"></div></td>
                <td class="td"><div class="skeleton h-4 w-20 ml-auto"></div></td>
                <td class="td"><div class="skeleton h-4 w-24 ml-auto"></div></td>
                <td class="td"><div class="skeleton h-4 w-28 ml-auto"></div></td>
              </tr>
            {/each}
          {:else if $portfolio.positions.length === 0}
            <tr>
              <td colspan="4" class="td">
                <div class="py-10 text-center">
                  <i class="fas fa-chart-pie text-2xl text-ink-tertiary mb-3"></i>
                  <p class="text-sm text-ink-secondary">No positions yet. Create a pie and invest to get started.</p>
                </div>
              </td>
            </tr>
          {:else}
            {#each $portfolio.positions as position}
              <tr class="hover:bg-canvas">
                <td class="td">
                  <span class="text-sm font-semibold text-ink">{position.symbol}</span>
                </td>
                <td class="td text-right tnum">
                  {parseFloat(position.qty).toFixed(4)}
                </td>
                <td class="td text-right tnum">
                  {formatCurrency(position.market_value)}
                </td>
                <td class="td text-right tnum">
                  <span class="{parseFloat(position.unrealized_pl) >= 0 ? 'text-emerald-600' : 'text-red-600'}">
                    {formatCurrency(position.unrealized_pl)} ({parseFloat(position.unrealized_plpc * 100).toFixed(2)}%)
                  </span>
                </td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>
  </div>
  <div>
    <CardPieChart title="Portfolio Allocation" data={portfolioAllocation} />
  </div>
</div>
